import { GeoCoordinates, Site } from '@domain/graphql.types';
import { accessToken } from '@infrastructure/mapbox';
import { feature, featureCollection, bbox as turfBoundingBox, circle as turfCircle, distance as turfDistance, lineString as turfLineString } from '@turf/turf';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { LngLatBoundsLike, MapRef } from 'react-map-gl/mapbox';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { useSitesRequest } from '@domain/index';
import { SearchFilters, toSearchFiltersInput } from '../filters/searchFilters';

const unitedStatesCenter: GeoCoordinates = {
  longitude: -98,
  latitude: 39,
};
const unitedStatesOnlyZoom = 4.75;

const getTurfCircle = (center: GeoCoordinates, radius: number) => turfCircle([center.longitude, center.latitude], radius, { units: 'miles' });

const getTurfBoundingBox = (center: GeoCoordinates, radius: number): LngLatBoundsLike => {
  return turfBoundingBox(getTurfCircle(center, radius)) as LngLatBoundsLike;
};

/** room kept between a selected pin and the map edges (the selected pin is 52px tall) */
const selectedPinMargin = 64;

/** the pixel offset that brings a point inside the map's margins, or undefined when it already is */
export const getOffsetIntoView = (
  { x, y }: { x: number; y: number },
  { clientWidth, clientHeight }: { clientWidth: number; clientHeight: number },
): [number, number] | undefined => {
  const axisOffset = (value: number, size: number) => {
    const margin = Math.min(selectedPinMargin, size / 2);

    if (value < margin) {
      return value - margin;
    }

    if (value > size - margin) {
      return value - (size - margin);
    }

    return 0;
  };

  const dx = axisOffset(x, clientWidth);
  const dy = axisOffset(y, clientHeight);

  return dx || dy ? [dx, dy] : undefined;
};

type Props = {
  /** applied filters, never the draft: they also restrict the sites shown outside the radius */
  appliedFilters: SearchFilters;
  center?: GeoCoordinates;
  searchRadius: number;
  sites?: readonly Site[];
};

const useSiteLookupMap = ({ appliedFilters, center, searchRadius, sites }: Props) => {
  const { selectedSite, setSelectedSite, setIsMapExtended } = useContext(SiteLookupMapContext);

  const mapRef = useRef<MapRef>(null);

  // Outside the radius the background sites follow the same filters, and are hidden while they reload.
  const {
    data,
    loading: outOfRadiusSitesLoading,
    error: outOfRadiusSitesError,
    retry: retryOutOfRadiusSites,
  } = useSitesRequest(toSearchFiltersInput(appliedFilters));

  // Inside the searched radius only the search results are shown (as markers), so stations excluded
  // by the filters disappear from the map and the clusters are recomputed without them.
  const allSites = useMemo(() => {
    let allSites = data || [];

    if (center) {
      allSites = allSites.filter(
        ({ coordinates }) =>
          turfDistance([center.longitude, center.latitude], [coordinates.longitude, coordinates.latitude], { units: 'miles' }) > searchRadius,
      );
    }

    const features =
      allSites.map(({ coordinates, includedInReport, trackingState, status }) =>
        feature({ type: 'Point', coordinates: [coordinates.longitude, coordinates.latitude] }, { includedInReport, trackingState, status }),
      ) || [];

    return featureCollection(features);
  }, [center, data, searchRadius]);

  useEffect(() => {
    if (!center) {
      return;
    }

    setIsMapExtended(false);
  }, [center, setIsMapExtended]);

  // Move only when the selection changes: new results (e.g. applied filters) keep the centre and zoom.
  const sitesRef = useRef(sites);
  sitesRef.current = sites;
  useEffect(() => {
    if (!selectedSite || selectedSite.origin === 'map') {
      return;
    }

    const coords = sitesRef.current?.find(({ id }) => id === selectedSite.id)?.coordinates;

    if (!coords) {
      return;
    }

    const map = mapRef.current;

    if (!map) {
      return;
    }

    // Keep the map still while the pin is visible; otherwise pan only as far as needed to bring it in.
    const offset = getOffsetIntoView(map.project([coords.longitude, coords.latitude]), map.getContainer());

    if (offset) {
      map.panBy(offset);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (!center) {
      return;
    }

    setTimeout(() => mapRef.current?.fitBounds(getTurfBoundingBox(center, searchRadius)), 50);
  }, [center, searchRadius]);

  const [radiusCircle, radiusLine] = useMemo(() => {
    if (!center) {
      return [];
    }

    const circle = getTurfCircle(center, searchRadius);

    return [circle, turfLineString(circle.geometry.coordinates.flat())];
  }, [center, searchRadius]);

  return {
    actions: {
      retryOutOfRadiusSites,
      setSelectedSite,
    },
    refs: {
      map: mapRef,
    },
    state: {
      allSites,
      displayExtendControl: !!center,
      mapCenter: center || unitedStatesCenter,
      mapZoom: unitedStatesOnlyZoom,
      outOfRadiusSitesError,
      outOfRadiusSitesLoading,
      selectedSite,
    },
    layers: {
      radiusCircle,
      radiusLine,
    },
    map: {
      accessToken,
      style: import.meta.env.VITE_REACT_APP_MAP_BOX_MAP_STYLE,
    },
  };
};

export default useSiteLookupMap;
